import React, { useEffect, useState } from 'react';
import { Stack, Form, Tab, Tabs, Row } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useGetEmployeeListQuery, useGetFollowersCompanyQuery } from 'services';
import { useQueryClient } from '@tanstack/react-query';
import useAuthStore, { updateUserInfo } from 'store/auth';
import useSkeleton from 'hooks/useSkeleton';
import PaginationBox from 'components/PaginationBox';
import MembersSkeleton from 'components/Common/Skeleton/MembersSkeleton';
import CompanyFollowers from 'components/Followers/CompanyFollowrs/FollowingCompanies';
import EmployeesList from 'components/Followers/Employees/EmployeesList';
import useTranslate from 'hooks/useTranslate';
import { dataLimits } from 'constants';
import './styles.scss';

const FollowerList: React.FC = () => {
	const { translate } = useTranslate();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const delayedLoading = useSkeleton(isLoading);
	const queryClient = useQueryClient();
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTabKey: any = searchParams.get('activeTab') ?? 'followers';
	const [startSearch, setStartSearch] = useState<boolean>(false);
	const userId = searchParams.get('userId') ?? '';

	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	const [userData, setUserData] = useState({
		currentPage: 1,
		limit: dataLimits.limits,
		order: 'DESC',
		connectionType: activeTabKey,
		keyword: searchParams.get('value') ?? '',
		id: userId || user?.userId,
	});

	const { data: { docs: employeeData = [], meta = {} } = {} }: any =
		useGetEmployeeListQuery(userData);
	const {
		data: { docs: FollowingsCompanyData = [], meta: followingCompaniesMeta = {} } = {},
	}: any = useGetFollowersCompanyQuery(userData);

	useEffect(() => {
		updateUserInfo({ employees: meta?.totalDocs });
	}, [meta?.totalDocs]);

	useEffect(() => {
		setIsLoading(true);
		if (userData?.connectionType === 'employees') {
			queryClient.invalidateQueries({ queryKey: [`employee-list`] }).finally(() => {
				setIsLoading(false);
				updateUserInfo({ employees: meta?.totalDocs });
			});
		} else {
			queryClient
				.invalidateQueries({
					queryKey: [`followers-company`],
				})
				.finally(() => {
					setIsLoading(false);
					updateUserInfo({ follower: followingCompaniesMeta?.totalDocs });
				});
		}
	}, [userData.currentPage, startSearch, activeTabKey, userId]);

	const handleCurrentPage = (e: any) => {
		setUserData((prev) => ({
			...prev,
			currentPage: e,
		}));
	};

	const handleTabChange = (e: any) => {
		setUserData((prev) => ({
			...prev,
			connectionType: e,
			currentPage: 1,
		}));
		setSearchParams({ activeTab: e, value: userData?.keyword ?? '' });
	};

	const handleSearch = (e: any) => {
		if (e.keyCode === 13 && e.target.value) {
			searchParams.set('value', e.target.value.trim());
			setSearchParams(searchParams);
			setStartSearch((prev) => !prev);
			setUserData((prev) => ({
				...prev,
				keyword: e?.target.value.trim(),
			}));
		}
	};

	const handleOnChange = (e: any) => {
		setSearchParams({ activeTab: userData?.connectionType, value: e.target.value });
		if (e?.target?.value.length === 0) {
			setStartSearch((prev) => !prev);
			setUserData((prev) => ({
				...prev,
				keyword: e?.target.value,
			}));
		} else {
			setUserData((prev) => ({
				...prev,
				keyword: e?.target.value,
			}));
		}
	};

	return (
		<div className="white-box bg-white">
			<Stack className="gap-3 gap-md-4">
				<div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
					<div className="list-heading">
						<h4 className="fw-bold">{translate('Profile.followersNdEmployees')}</h4>
					</div>
					<Form.Group className="user-search position-relative w-100">
						<i className="ri-search-line position-absolute"></i>
						<Form.Control
							className="rounded-pill"
							type="search"
							placeholder="Search by Followers/Employees Name"
							onKeyDown={handleSearch}
							onChange={handleOnChange}
							value={userData.keyword ?? ''}
						/>
					</Form.Group>
				</div>
				<div className="connections-tab">
					<Tabs
						defaultActiveKey={activeTabKey}
						id="uncontrolled-tab-example"
						onSelect={handleTabChange}
						activeKey={activeTabKey}
					>
						<Tab eventKey="followers" title={translate('Common.followers')}>
							<div>
								<h6 className="mb-3">
									{translate('Common.followers')}{' '}
									<span className="fw-bold">({followingCompaniesMeta?.totalDocs})</span>
								</h6>
								<Row className="connections-row">
									{delayedLoading ? (
										<MembersSkeleton />
									) : (
										<CompanyFollowers
											FollowingsCompanyData={FollowingsCompanyData}
											unFollowCompany={null}
										/>
									)}
								</Row>
							</div>
						</Tab>
						<Tab eventKey="employees" title={translate('Common.employees')}>
							<div>
								<h6 className="mb-3">
									{translate('Common.employees')}{' '}
									<span className="fw-bold">({meta?.totalDocs})</span>
								</h6>
								<Row className="connections-row">
									{delayedLoading ? (
										<MembersSkeleton />
									) : (
										<EmployeesList FollowingsCompanyData={employeeData} unFollowCompany={null} />
									)}
								</Row>
							</div>
						</Tab>
					</Tabs>
				</div>
			</Stack>
			{['employees'].includes(userData.connectionType) ? (
				<PaginationBox
					totalData={meta?.totalDocs}
					limit={meta?.limit}
					page={userData.currentPage}
					setCurrentPage={handleCurrentPage}
				/>
			) : (
				<PaginationBox
					totalData={followingCompaniesMeta?.totalDocs}
					limit={followingCompaniesMeta?.limit}
					page={userData.currentPage}
					setCurrentPage={handleCurrentPage}
				/>
			)}
		</div>
	);
};

export default FollowerList;
